import Vue from "vue";
import VueRouter from "vue-router";
import VueCookies from "vue-cookies-reactive";
import * as Sentry from "@sentry/vue";
import { Integrations } from "@sentry/tracing";
import { CaptureConsole as CaptureConsoleIntegration } from "@sentry/integrations";

import gql from "graphql-tag";

// import DashboardPlugin from "./material-dashboard";

// Plugins
import App from "./App.vue";
import Chartist from "chartist";
import vueBraintree from "vue-braintree";
import Fragment from "vue-fragment";
//import Particles from "particles.vue";
import VueApexCharts from "vue-apexcharts";
import VTooltip from "v-tooltip";
import VueIntercom from "vue-intercom";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// Style
import "./assets/scss/custom/main.scss";

// router setup
import routes from "./routes/routes";

// store
import store from "./store";

// GraphQL
import VueApollo from "vue-apollo";
import ApolloClient from "apollo-client";
import { SubscriptionClient } from "subscriptions-transport-ws";
import { WebSocketLink } from "apollo-link-ws";
import { InMemoryCache } from "apollo-cache-inmemory";
// import * as Notifications from "./components/NotificationPlugin";
import * as VueClipboard from "vue-clipboard2";
import { createHttpLink } from "apollo-link-http";
import { ApolloLink } from "apollo-boost";
import { onError } from "apollo-link-error";
import { setContext } from "apollo-link-context";

import {
  faGlobe,
  faArrowRight,
  faDownload,
  faLock,
  faAngleDoubleUp,
  faInfinity,
  faCommentSms,
  faAddressCard,
  faPaperPlane,
  faPhoneFlip,
  faHashtag,
  faRotate,
  faCloudArrowUp,
  faMobileScreenButton,
  faEllipsisH,
  faGear,
} from "@fortawesome/free-solid-svg-icons";

import {
  faTiktok,
  faTwitter,
  faTwitch,
  faInstagram,
  faFacebook,
} from "@fortawesome/free-brands-svg-icons";

import {
  faEnvelope,
  faComment,
  faCirclePlay,
  faClock,
  faPaperPlane as faPaperPlaneRegular,
  faTrashAlt,
  faCircleCheck,
  faCircleXmark,
} from "@fortawesome/free-regular-svg-icons";

library.add(
  faGlobe,
  faArrowRight,
  faDownload,
  faEnvelope,
  faComment,
  faLock,
  faAngleDoubleUp,
  faCirclePlay,
  faInfinity,
  faCommentSms,
  faAddressCard,
  faPaperPlane,
  faPhoneFlip,
  faHashtag,
  faRotate,
  faCloudArrowUp,
  faTiktok,
  faTwitter,
  faTwitch,
  faInstagram,
  faFacebook,
  faMobileScreenButton,
  faClock,
  faPaperPlaneRegular,
  faTrashAlt,
  faEllipsisH,
  faCircleCheck,
  faCircleXmark,
  faGear
);

Vue.component("font-awesome-icon", FontAwesomeIcon);

// plugin setup
Vue.use(VueRouter);
Vue.use(VueCookies);
// Vue.use(DashboardPlugin);
Vue.use(VueApollo);
// Vue.use(Notifications);
Vue.use(VueClipboard);
Vue.use(vueBraintree);
Vue.use(Fragment.Plugin);
//Vue.use(Particles);
Vue.use(VueApexCharts);
Vue.use(VTooltip);
Vue.use(VueIntercom, {
  appId: "szv09da9",
  custom_launcher_selector: ".intercom-launcher",
});

Vue.directive("click-outside", {
  bind: function(el, binding, vnode) {
    el.clickOutsideEvent = function(event) {
      if (!(el == event.target || el.contains(event.target))) {
        vnode.context[binding.expression](event);
      }
    };
    document.body.addEventListener("click", el.clickOutsideEvent);
  },
  unbind: function(el) {
    document.body.removeEventListener("click", el.clickOutsideEvent);
  },
});

// configure router
const router = new VueRouter({
  mode: "history",
  routes, // short for routes: routes
  scrollBehavior: (to) => {
    if (to.hash) {
      return { selector: to.hash };
    } else {
      return { x: 0, y: 0 };
    }
  },
  linkExactActiveClass: "nav-item active",
});

Sentry.init({
  Vue,
  dsn:
    "https://a4f74c24d7a3458399d0f30cf38c99eb@o468136.ingest.sentry.io/5495596",
  integrations: [
    new CaptureConsoleIntegration({
      levels: ["error"],
    }),
    new Integrations.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: ["localhost", "api.artisthub.io", /^\//],
    }),
    new Sentry.Replay({
      networkDetailAllowUrls: ['properties://browser/clickID'],
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 1.0,
  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,
  async beforeSend(event) {
    if (event.exception) {
      event.extra = {
        ...event.extra,
        state: store.state,
      };
    }
    return event;
  },
});

router.beforeEach((to, from, next) => {
  const user = store.getters.getBillingUser || {};
  const artist = store.getters.getArtist || {};

  if (["family", "agency"].includes(user.plan)) return next();

  const isLoggedIn = !!user.id;
  const invalidUserStatus =
    user.braintree_subscription_status &&
    !["Pending", "Active", "Trial"].includes(
      user.braintree_subscription_status
    ) &&
    new Date(user.next_billing_date) < new Date();
  const trialEnded = user.braintree_subscription_status === "Trial" &&
    new Date(user.first_billing_date) < new Date();
  const invalidArtistPlan = !artist.current_plan;
  const notBillingOrLogout = !["Billing", "Logout"].includes(to.name);
  const allowNewArtist = to.name === "AddArtist" && !invalidUserStatus;

  if (
    isLoggedIn &&
    (invalidUserStatus || invalidArtistPlan || trialEnded) &&
    notBillingOrLogout &&
    !allowNewArtist
  ) {
    next({ name: "Billing" });
  } else {
    next();
  }
});

// global library setup
Vue.prototype.$Chartist = Chartist;

// graphql
const backendUrl = "wss://api.artisthub.io/v1/graphql";

let lastError = null;
let errorCount = 0;
let totalRecentErrorCount = 0;
let lastErrorAt = null;

// eslint-disable-next-line no-unused-vars
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const now = new Date();
  if (lastErrorAt && now - lastErrorAt <= 4000) {
    totalRecentErrorCount++;
  } else totalRecentErrorCount = 0;
  lastErrorAt = now;
  console.log({
    operationName: operation.operationName,
    variables: JSON.stringify(operation.variables || {}),
    query: operation.query.loc?.source.body,
  });

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.log(`GraphQL error`, locations, path);
      console.error(`GraphQL error: ${message}`);
      console.log(JSON.stringify(store.state));

      if (lastError === message) {
        errorCount += 1;
      } else {
        lastError = message;
        errorCount = 1;
      }

      if (errorCount >= 3) {
        localStorage.clear();
        store.dispatch("resetState");

        errorCount = 0;
      }
    });
  }

  if (networkError) {
    console.error(`Network error: ${networkError.message}`);
    console.log(store.state);
    if (lastError === networkError?.extensions?.code) {
      errorCount += 1;
    } else {
      lastError = networkError?.extensions?.code || null;
      errorCount = 1;
    }

    if (errorCount >= 3) {
      localStorage.clear();
      store.dispatch("resetState");

      errorCount = 0;
    }
  }

  if (totalRecentErrorCount >= 5) {
    localStorage.clear();
    store.dispatch("resetState");

    location.reload();

    errorCount = 0;
    totalRecentErrorCount = 0;
  }
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem("token");
  // return the headers to the context so httpLink can read them
  if (token && token.length && token !== null && token !== "null") {
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
    };
  } else {
    return {
      headers: {
        ...headers,
      },
    };
  }
});

// connection
export const wsClient = new SubscriptionClient(backendUrl, {
  // lazy: true,
  reconnect: true,
  timeout: 30000,
  connectionParams: () => {
    // console.log("websocket connectionParams");
    const token = localStorage.getItem("token");
    if (token && token.length && token !== null && token !== "null") {
      return { headers: { Authorization: `Bearer ${token}` } };
    } else {
      return {};
    }
  },
});
Vue.prototype.$apollo_wsClient = wsClient;
const link = new WebSocketLink(wsClient);
export const client = new ApolloClient({
  link: ApolloLink.from([errorLink, link]),
  cache: new InMemoryCache({
    addTypename: true,
  }),
});
// HTTP connection to the remote API
const remoteHttpLink = createHttpLink({
  uri: "https://api.artisthub.io/v1/graphql",
});
export const httpClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, remoteHttpLink]),
  cache: new InMemoryCache({
    addTypename: true,
  }),
});

// HTTP connection to the local API
const localHttpLink = createHttpLink({
  uri: process.env.VUE_APP_GRAPHQL_HTTP,
  // uri: "https://api.artisthub.io/v1/graphql"
  // headers: {
  //   authorization: `Bearer ${localStorage.getItem("token")}`
  // }
  // headers: getAuth2()
});

export const localClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, localHttpLink]),
  cache: new InMemoryCache({
    addTypename: true,
  }),
});

const apolloProvider = new VueApollo({
  clients: {
    client,
    localClient,
    httpClient,
  },
  defaultClient: client,
  // defaultOptions: {
  //   link: authLink
  // }
});

Vue.mixin({
  methods: {
    subscriptionActive: (artist) => {
      const { braintree_subscription_status: status } = artist;
      if (status == "Canceled") return false;

      return true;
    },
    destinationUrlValid: (type, url) => {
      // if (
      //   type === "spotify" &&
      //   !url.match(
      //     /^https:\/\/open\.spotify\.com\/(album|track|artist|playlist)\/[a-zA-Z0-9]+(.*)?$/
      //   )
      // ) {
      //   return false;
      // }

      // if (
      //   type === "applemusic" &&
      //   !url.match(
      //     /^https:\/\/music\.apple\.com\/([a-z]{2})\/(album|artist|playlist|track)\/([a-zA-Z0-9._-]+)(.*)?$/
      //   )
      // ) {
      //   return false;
      // }

      // if (
      //   type === "amazonmusic" &&
      //   !url.match(
      //     /^https:\/\/music\.amazon\.com\/(albums|artists|playlists|songs)\/([a-zA-Z0-9._-]+)(.*)?$/
      //   )
      // ) {
      //   return false;
      // }

      // if (
      //   type === "deezer" &&
      //   !url.match(
      //     /^https:\/\/www\.deezer\.com\/(track|album|artist|playlist)\/([0-9]+)(.*)?$/
      //   )
      // ) {
      //   return false;
      // }

      // if (
      //   type === "itunes" &&
      //   !url.match(
      //     /^https:\/\/itunes\.apple\.com\/([a-z]{2})\/(album|artist|playlist|track)\/([a-zA-Z0-9._-]+)(.*)?$/
      //   )
      // ) {
      //   return false;
      // }

      // if (
      //   type === "pandora" &&
      //   !url.match(
      //     /^https:\/\/www\.pandora\.com\/(artist|playlist)\/([a-zA-Z0-9._-]+)\/([a-zA-Z0-9._-]+)(.*)?$/
      //   )
      // ) {
      //   return false;
      // }

      // if (
      //   type === "tidal" &&
      //   !url.match(
      //     /^https:\/\/tidal\.com\/browse\/(track|album|artist|playlist)\/([0-9]+)(.*)?$/
      //   )
      // ) {
      //   return false;
      // }

      // if (
      //   type === "soundcloud" &&
      //   !url.match(
      //     /^https:\/\/soundcloud\.com\/([a-zA-Z0-9._-]+)\/(sets\/)?([a-zA-Z0-9._-]+)(.*)?$/
      //   )
      // ) {
      //   return false;
      // }

      // if (
      //   type === "youtube" &&
      //   !url.match(
      //     /^https:\/\/www\.youtube\.com\/(channel|watch)\?v=([a-zA-Z0-9._-]+)(.*)?$/
      //   )
      // ) {
      //   return false;
      // }

      // if (
      //   type === "bandcamp" &&
      //   !url.match(
      //     /^https:\/\/([a-zA-Z0-9._-]+)\.bandcamp\.com\/(album|track|artist|playlist)\/([a-zA-Z0-9._-]+)(.*)?$/
      //   )
      // ) {
      //   return false;
      // }

      // if (
      //   type === "melon" &&
      //   !url.match(
      //     /^https:\/\/www\.melon\.com\/(song|album|artist|playlist)\/detail\.htm\?([a-zA-Z0-9._-]+)=([0-9]+)(.*)?$/
      //   )
      // ) {
      //   return false;
      // }

      // if (
      //   type === "resso" &&
      //   !url.match(/^https:\/\/m\.resso\.app\/([a-zA-Z0-9._-]+)(.*)?$/)
      // ) {
      //   return false;
      // }

      console.log(type, url);

      return true;
    },
  },
});

/* eslint-disable no-new */
new Vue({
  el: "#app",
  render: (h) => h(App),
  router,
  store,
  apolloProvider,
  async mounted() {
    const user = this.$store.getters.getUser || {};
    const isLoginPage = this.$route.name === "Login";

    if (isLoginPage || !user.email) {
      this.$intercom.boot({
        app_id: "szv09da9",
        hide_default_launcher: false,
      });
    } else {
      this.$intercom.boot({
        app_id: "szv09da9",
        hide_default_launcher: true,
      });
    }

    if (!user.email) {
      this.$intercom.boot();
      Sentry.setUser(null);
    } else {
      const { id: user_id, email } = user;

      const result = await this.$apollo.mutate({
        client: "localClient",
        mutation: gql`mutation { intercom_hash }`,
        variables: {}
      });

      this.$intercom.boot({
        user_id,
        email,
        user_hash: result.data.intercom_hash
      });

      Sentry.setUser({ id: user_id, email });
    }
  },
  // },
  // watch: {
  //   $route() {
  //     const artist = this.$store.getters.getArtist || null;
  //     if (artist && !artist.current_plan) {
  //       return this.$router.push({ name: "Billing" });
  //     }
  //   }
  // }
});
